import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatNumber(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '0';
  return new Intl.NumberFormat('id-ID').format(n);
}

/** 1 kwintal = 100 kilogram. */
export const KG_PER_KWINTAL = 100;

/** Massa dalam kilogram → teks "kg (kwintal)" (kwintal hanya bila ≥ 1). */
export function formatWeightKg(value: number | string | null | undefined): string {
  const kg = Number(value ?? 0);
  if (!Number.isFinite(kg)) return '0 kg';
  const kgText = `${formatNumber(kg)} kg`;
  const kwintal = kg / KG_PER_KWINTAL;
  return kwintal >= 1 ? `${kgText} (${formatNumber(kwintal)} kwintal)` : kgText;
}

/** Massa dalam kilogram yang dinyatakan sebagai kwintal. */
export function formatKwintal(value: number | string | null | undefined): string {
  const kg = Number(value ?? 0);
  if (!Number.isFinite(kg)) return '0 kwintal';
  return `${formatNumber(kg / KG_PER_KWINTAL)} kwintal`;
}

/** Normalize an Indonesian phone number to the international `62…` form. */
export function normalizePhoneId(value: string | null | undefined): string | null {
  if (!value) return null;
  const digits = value.replace(/\D+/g, '');
  if (!digits) return null;
  if (digits.startsWith('62')) return digits;
  if (digits.startsWith('0')) return `62${digits.slice(1)}`;
  if (digits.startsWith('8')) return `62${digits}`;
  return digits;
}

/**
 * Click-to-chat WhatsApp deep link, or `null` when the number is missing/invalid.
 * Opens in the WhatsApp app on mobile and WhatsApp Web on desktop.
 */
export function whatsappLink(value: string | null | undefined, message?: string): string | null {
  const phone = normalizePhoneId(value);
  if (!phone) return null;
  const text = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${phone}${text}`;
}

/**
 * True when `value` looks like an Indonesian phone number rather than an
 * e-mail address or free text — used to decide whether a WhatsApp deep link
 * makes sense for a generic "contact" field.
 */
export function isPhoneNumber(value: string | null | undefined): boolean {
  if (!value) return false;
  const raw = value.trim();
  if (raw.includes('@') || /[a-z]/i.test(raw)) return false;
  const digits = raw.replace(/\D+/g, '');
  return digits.length >= 9 && digits.length <= 15;
}

/** Human label for an enum-ish value (SCREAMING_SNAKE -> Title Case). */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Human-readable file size (e.g. 1.2 MB). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}
